import { Variants } from '@amplitude/experiment-js-client';
import { IntlShape } from '@liveauctioneers/hammer-ui-core/intl';
import { createSelector } from '@reduxjs/toolkit';

import { postSetPassword } from '@/redux/modules/account/createAccount/createAccount.api';
import { submitLogin } from '@/redux/modules/account/login/login.actions';
import { getAuthToken, getBidderEmail, getBidderId, getUserData } from '@/redux/modules/account/user/user.selectors';
import { setNewSignupSearchParams, submitSaveSearch } from '@/redux/modules/search/save/saveSearch.actions';
import { getNewSignupSearchParams } from '@/redux/modules/search/save/saveSearch.selectors';
import { AppDispatch, AppGetState, GlobalState } from '@/redux/store';
import { FeatureFlags } from '@/types/FeatureFlags';
import { getInvalidFacetsToast } from '@/utils/saveSearch/getInvalidFacetsToast';

import { LOG_OUT_BIDDER } from './account/logout/logout.actions';
import { DISMISS_MODAL } from './actions';
import { AttributionData, getSignupCompletedAnalytics } from './analytics';
import { getDeployment } from './config';

/* Action Types */
export const SET_PASSWORD_FAIL = 'la/ui/setPassword/FAIL';
export const SET_PASSWORD_REQUEST = 'la/ui/setPassword/REQUEST';
export const SET_PASSWORD_SUCCESS = 'la/ui/setPassword/SUCCESS';

// reducer
export type SetPasswordSlice = {
    error: boolean;
    errorMessage: string;
    submitted: boolean;
    success: boolean;
};

export const defaultSetPasswordSlice: SetPasswordSlice = {
    error: false,
    errorMessage: '',
    submitted: false,
    success: false,
};

export default function reducer(state: SetPasswordSlice = defaultSetPasswordSlice, action: any = {}): SetPasswordSlice {
    switch (action.type) {
        case SET_PASSWORD_FAIL:
            return {
                ...state,
                error: true,
                errorMessage: action.payload,
                submitted: false,
                success: false,
            };
        case SET_PASSWORD_REQUEST:
            return {
                ...state,
                error: false,
                errorMessage: '',
                submitted: true,
                success: false,
            };
        case SET_PASSWORD_SUCCESS:
            return {
                ...state,
                error: false,
                errorMessage: '',
                submitted: false,
                success: true,
            };
        case DISMISS_MODAL:
        case LOG_OUT_BIDDER:
            return { ...defaultSetPasswordSlice };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state;

export const getUiSetPassword = createSelector(stateSelector, (state) => state.setPassword);

/* ACTION CREATORS */
export const submitSetPassword =
    (
        password: string,
        attributionData: AttributionData,
        formatMessage: IntlShape['formatMessage'],
        amplitudeExperimentFlags: Variants,
        featureFlags: FeatureFlags,
        shouldCloseModal?: boolean
    ) =>
    async (dispatch: AppDispatch, getState: AppGetState) => {
        let emailAddress = '';
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);
            emailAddress = getBidderEmail(state);
            const userData = getUserData(state);

            await dispatch({
                type: SET_PASSWORD_REQUEST,
            });
            await postSetPassword({ authToken, deployment, password });

            // bidderID from token not response

            await dispatch({
                meta: {
                    actionTime: Date.now(),
                },
                payload: userData,
                type: SET_PASSWORD_SUCCESS,
            });
            await dispatch(
                submitLogin({
                    amplitudeExperimentFlags,
                    emailAddress,
                    featureFlags,
                    location: {
                        hash: '',
                        key: '',
                        pathname: '',
                        search: '',
                        state: null,
                    },
                    // this form is for the account creation process, so it will always be a new account
                    newAccount: true,
                    password,
                    shouldCloseModal,
                })
            );

            getSignupCompletedAnalytics({
                bidderId: getBidderId(getState()),
            });

            // submit save search if this completes a new account signup and a search exists
            const newSignupSearchParams = getNewSignupSearchParams(state);
            if (Object.keys(newSignupSearchParams).length > 0) {
                await dispatch(
                    submitSaveSearch({
                        feedback: {
                            invalidFacets: getInvalidFacetsToast(formatMessage),
                            saved: {
                                description: formatMessage({ id: 'toastMessages.descriptions.followingSearch' }),
                                title: formatMessage({ id: 'toastMessages.titles.success' }),
                            },
                        },
                        saveSearchParams: newSignupSearchParams,
                        sendAlert: false,
                    })
                );
                dispatch(setNewSignupSearchParams({}));
            }
        } catch (error) {
            dispatch({
                error: true,
                meta: { emailAddress },
                payload: error.error_message || error.message || error,
                type: SET_PASSWORD_FAIL,
            });
        }
    };
